import type { FeedbackReason } from "./types";

/** Reconnect backoff (spec §5.2: 1/2/5/10 s, then manual retry). */
export const STREAM_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000] as const;
/** No bytes within this window → assume a buffering proxy / in-app browser and switch to polling. */
export const STREAM_FIRST_BYTE_TIMEOUT_MS = 8000;
export const POLL_INTERVAL_MS = 1000;
export const SSE_CONTENT_TYPE = "text/event-stream";
export const HTTP_TOO_MANY_REQUESTS = 429;
export const RETRY_AFTER_HEADER = "Retry-After";

/** Follow-up suggestions shown under an answer (a longer list reads as a menu, not a hint). */
export const CHAT_MAX_FOLLOWUPS = 3;

/** The neutral "Thinking… / Working on it…" status moves to its next phrase this often. */
export const STATUS_ROTATE_MS = 2500;
/** The status's live region is filled this long after it appears: text already inside a region when it is inserted is not announced by every screen reader. */
export const STATUS_ANNOUNCE_DELAY_MS = 100;

export const COMPOSER_MAX_CHARS = 2000;
/** Counter appears once this share of the limit is used. */
export const COMPOSER_COUNTER_FROM = 0.8;

/** Exact feedback reason labels (spec §5.3). */
export const FEEDBACK_REASONS: readonly { value: FeedbackReason; label: string }[] = [
  { value: "wrong_info", label: "Wrong info" },
  { value: "outdated", label: "Outdated" },
  { value: "not_what_i_asked", label: "Not what I asked" },
  { value: "too_long", label: "Too long" },
];
export const FEEDBACK_NOTE_MAX_CHARS = 500;

/**
 * Live conversation events (`GET /public/v1/conversations/{id}/events`, DEC-059): one contract for
 * the backend publishers (`app/schemas/conversation_events.py`) and every client decoder.
 */
export const CONVERSATION_EVENT = { handoffStatus: "handoff_status", message: "message", typing: "typing" } as const;
export const CONVERSATION_EVENTS = [CONVERSATION_EVENT.handoffStatus, CONVERSATION_EVENT.message, CONVERSATION_EVENT.typing] as const;
export type ConversationEventName = (typeof CONVERSATION_EVENTS)[number];

/** `handoff_status.state`: requested → queued → joined → resolved | ai_resumed. */
export const HANDOFF_STATE = { requested: "requested", queued: "queued", joined: "joined", resolved: "resolved", aiResumed: "ai_resumed" } as const;
export const HANDOFF_STATES = [HANDOFF_STATE.requested, HANDOFF_STATE.queued, HANDOFF_STATE.joined, HANDOFF_STATE.resolved, HANDOFF_STATE.aiResumed] as const;
export type HandoffState = (typeof HANDOFF_STATES)[number];

/** `message.role` / `typing.role` on the live channel. */
export const LIVE_ROLE = { staff: "staff", assistant: "assistant", system: "system" } as const;
export const LIVE_ROLES = [LIVE_ROLE.staff, LIVE_ROLE.assistant, LIVE_ROLE.system] as const;
export type LiveRole = (typeof LIVE_ROLES)[number];

/** A "<name> is typing" indicator disappears after this long without a refresh. */
export const TYPING_TTL_MS = 8000;

/** Allowed link schemes in rendered answers; everything else renders as plain text. */
export const SAFE_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"] as const;

/** Tour confirmation copy defaults (spec §5.3). */
export const TOUR_RESULT_BODY = "Meet at the Admissions Lobby, Gate 1. We sent a confirmation by email.";
